import { useRef, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import {
  FiTrash2,
  FiUploadCloud,
  FiRefreshCw,
} from "react-icons/fi";
import { IKImage, IKContext, IKUpload } from "imagekitio-react";
import {
  urlEndpoint,
  authEndpoint,
  pubKeyImageKit,
} from "../lib/libraries/imageKit/ImageKit";
import GenerateImage from "../pages/doseeData/utils/GenerateImage";

// Sparkles icon for AI-generate actions
const SparklesIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.75l-1.06 2.121a.75.75 0 01-1.346 0L6.349 15.75l-2.121-1.06a.75.75 0 010-1.346l2.121-1.06 1.058-2.122a.75.75 0 011.346 0l1.06 2.122 2.121 1.06a.75.75 0 010 1.346L9.813 15.75z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17.813 8.25l-.53 1.061a.375.375 0 01-.673 0l-.53-1.061-1.06-.53a.375.375 0 010-.673l1.06-.53.53-1.061a.375.375 0 01.673 0l.53 1.06 1.061.531a.375.375 0 010 .673l-1.061.53z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20.813 15.75l-.53 1.061a.375.375 0 01-.673 0l-.53-1.061-1.06-.53a.375.375 0 010-.673l1.06-.53.53-1.061a.375.375 0 01.673 0l.53 1.06 1.061.531a.375.375 0 010 .673l-1.061.53z"
    />
  </svg>
);

type ImageKitUploadProps = {
  folder?: any;
  thumbnail?: any;
  onSuccess?: any;
  thumbnailId?: any;
  setThumbnail?: any;
  setThumbnailId?: any;
  isRestaurantModal?: boolean;
};

export const ImageKitUpload = ({
  folder,
  thumbnail,
  onSuccess,
  thumbnailId,
  setThumbnail,
  setThumbnailId,
  isRestaurantModal = false,
}: ImageKitUploadProps) => {
  const [loading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Stable unique id so multiple instances on a page each wire their
  // <label htmlFor> to the right hidden file input.
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = `ik-upload-${Math.random().toString(36).slice(2, 10)}`;
  }
  const inputId = idRef.current;

  // Native HTML5 drag & drop — inject the dropped file into IKUpload's
  // hidden input via DataTransfer and dispatch a synthetic change event so
  // imagekitio-react picks it up exactly as if the user clicked + selected.
  const onDragOver = (e: any) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    if (!isDragOver) setIsDragOver(true);
  };
  const onDragLeave = (e: any) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  const onDrop = (e: any) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    const input = document.getElementById(inputId);
    if (input instanceof HTMLInputElement) {
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      } catch {
        /* DataTransfer not supported — user can still click to upload */
      }
    }
  };

  return (
    <>
      {showImageModal ? (
        <GenerateImage
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          setShowImageModal={setShowImageModal}
        />
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">Thumbnail</h3>
            <p className="mt-0.5 text-[11px] text-gray-500">
              JPG, PNG or WEBP — up to 5 MB
            </p>
          </div>
          {thumbnail ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Image attached
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-600 ring-1 ring-gray-200">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
              No image yet
            </span>
          )}
        </header>

        {/* Body */}
        <div className="p-5">
          <IKContext
            urlEndpoint={urlEndpoint}
            publicKey={pubKeyImageKit}
            authenticationEndpoint={authEndpoint}
          >
            {/* Hidden file input — triggered by every label that uses htmlFor={inputId} */}
            <IKUpload
              id={inputId}
              fileName="image-upload.png"
              folder={folder}
              accept=".jpg,.jpeg,.png,.webp"
              onError={() => setLoading(false)}
              onChange={() => setLoading(true)}
              onClick={() => {
                setLoading(false);
                setThumbnail(null);
                setThumbnailId(null);
              }}
              onSuccess={(e: any) => {
                setLoading(false);
                setThumbnail(e.url);
                setThumbnailId(e.fileId);
                if (isRestaurantModal && onSuccess) onSuccess(e.url, e.fileId);
              }}
              className="sr-only"
            />

            {/* Preview / drop zone */}
            <div
              className="group relative mx-auto w-full max-w-lg overflow-hidden rounded-xl"
              style={{ aspectRatio: "16 / 9" }}
              onDragEnter={!thumbnail ? onDragOver : undefined}
              onDragOver={!thumbnail ? onDragOver : undefined}
              onDragLeave={!thumbnail ? onDragLeave : undefined}
              onDrop={!thumbnail ? onDrop : undefined}
            >
              {thumbnail ? (
                <>
                  <IKImage
                    src={thumbnail}
                    width="1600"
                    alt="Selected thumbnail"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  />

                  {/* Subtle bottom gradient (revealed on hover for ID + chrome) */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-gray-900/60 via-gray-900/15 to-transparent opacity-0 transition group-hover:opacity-100" />

                  {/* File ID — hover only */}
                  {thumbnailId ? (
                    <span className="pointer-events-none absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-md bg-gray-900/70 px-2 py-1 font-mono text-[10px] text-white/95 opacity-0 backdrop-blur-sm transition group-hover:opacity-100">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      {String(thumbnailId).slice(0, 8)}
                    </span>
                  ) : null}

                  {/* Glassy floating action bar (always visible) */}
                  <div className="absolute right-3 top-3 flex items-center rounded-xl border border-white/20 bg-gray-900/40 p-1 shadow-lg ring-1 ring-black/5 backdrop-blur-md">
                    <label
                      htmlFor={inputId}
                      title="Replace image"
                      className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-semibold text-white/95 transition hover:bg-white/15"
                    >
                      <FiRefreshCw className="h-3.5 w-3.5" />
                      Replace
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowImageModal(true)}
                      title="Generate with AI"
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-semibold text-white/95 transition hover:bg-white/15"
                    >
                      <SparklesIcon className="h-3.5 w-3.5" />
                      AI
                    </button>
                    <span className="mx-0.5 h-5 w-px bg-white/20" />
                    <button
                      type="button"
                      onClick={() => {
                        setThumbnail(null);
                        setThumbnailId(null);
                      }}
                      title="Remove image"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-white/95 transition hover:bg-red-500/40"
                    >
                      <FiTrash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              ) : (
                <label
                  htmlFor={inputId}
                  className={[
                    "relative flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed text-center transition-all duration-200",
                    "focus-within:ring-2 focus-within:ring-brand",
                    isDragOver
                      ? "scale-[1.005] border-brand bg-brand/5"
                      : "border-gray-200 bg-gray-50 hover:border-brand/40 hover:bg-white",
                  ].join(" ")}
                >
                  {/* Mesh gradient bg */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-xl opacity-80 transition-opacity"
                    style={{
                      background:
                        "radial-gradient(circle at 22% 18%, rgba(0,145,145,0.10), transparent 45%), radial-gradient(circle at 80% 82%, rgba(0,178,178,0.08), transparent 55%)",
                    }}
                  />

                  {/* Floating cloud with brand glow */}
                  <div className="relative">
                    <span
                      aria-hidden
                      className={[
                        "absolute inset-0 -z-10 m-auto h-16 w-16 rounded-2xl blur-xl transition-opacity",
                        isDragOver
                          ? "bg-brand/30 opacity-100"
                          : "bg-brand/15 opacity-70",
                      ].join(" ")}
                    />
                    <span
                      className={[
                        "relative inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 transition-all duration-200",
                        isDragOver
                          ? "scale-110 text-brand ring-brand/30"
                          : "text-gray-500 group-hover:scale-105 group-hover:text-brand",
                      ].join(" ")}
                    >
                      <FiUploadCloud className="h-7 w-7" />
                    </span>
                  </div>

                  <p className="relative mt-4 text-sm font-semibold text-gray-900">
                    {isDragOver ? "Release to upload" : "Drop your image here"}
                  </p>
                  <p className="relative mt-1 text-[11px] text-gray-500">
                    or{" "}
                    <span className="font-semibold text-brand">
                      click to browse
                    </span>
                  </p>

                  {/* Format pills */}
                  <div className="relative mt-4 flex flex-wrap items-center justify-center gap-1.5">
                    {["JPG", "PNG", "WEBP"].map((fmt) => (
                      <span
                        key={fmt}
                        className="rounded-md bg-white px-2 py-0.5 font-mono text-[10px] font-semibold text-gray-600 shadow-sm ring-1 ring-gray-200"
                      >
                        {fmt}
                      </span>
                    ))}
                    <span className="text-[10px] text-gray-300">·</span>
                    <span className="text-[10px] font-medium text-gray-500">
                      up to 5 MB
                    </span>
                  </div>
                </label>
              )}

              {/* Loading overlay */}
              {loading ? (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-sm">
                  <div className="relative inline-flex h-10 w-10 items-center justify-center">
                    <span className="absolute inset-0 animate-ping rounded-full bg-brand/30" />
                    <AiOutlineLoading className="relative h-7 w-7 animate-spin text-brand" />
                  </div>
                  <p className="text-[11px] font-semibold text-gray-700">
                    Uploading…
                  </p>
                </div>
              ) : null}
            </div>

            {/* Toolbar — only on empty state. On filled state, the floating
                glassy bar covers all actions, keeping the image breathing. */}
            {!thumbnail ? (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <label
                  htmlFor={inputId}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-brand/40 hover:bg-brand/5 hover:text-brand focus-within:ring-2 focus-within:ring-brand"
                >
                  <FiUploadCloud className="h-4 w-4" />
                  Choose file
                </label>
                <span className="text-[11px] text-gray-300">or</span>
                <button
                  type="button"
                  onClick={() => setShowImageModal(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-brand to-primary px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <SparklesIcon className="h-4 w-4" />
                  Generate with AI
                </button>
              </div>
            ) : null}
          </IKContext>
        </div>
      </div>
    </>
  );
};
